import React, { RefObject, useRef, useEffect, useState, useMemo } from 'react';
import { useFormikContext } from 'formik';
import {
  useTranslator,
  FormikInput as Input,
  useAppContext,
} from '@front-activerse/common-components';
import { Button } from 'reactstrap';
import { Player } from '@lordicon/react';

interface TrackerMealFormHeaderProps {
  showDateInput: boolean;
  minDate: string;
  maxDate: string;
  mealName: string;
  editPrimaryIcon: string;
  inputRef: RefObject<HTMLInputElement | null>;
  onDateBlur: (value: string | number) => void;
  /** Called when the time input changes; use to persist the user's value across Formik reinit. */
  onTimeChange?: (value: string | number) => void;
  /** Called when servings input changes; use to rescale ingredient amounts. */
  onServingsChange?: (newServings: number) => void;
  /** When false, hide date and time inputs. Default: true */
  showDateTimeInputs?: boolean;
  /** When false, hide servings input. Default: true */
  showServingsInput?: boolean;
  /** When true, show meal name input on mount and focus it (e.g. when mealData.name is empty). */
  initialShowMealNameEdit?: boolean;
}

const TrackerMealFormHeader: React.FC<TrackerMealFormHeaderProps> = ({
  showDateInput,
  minDate,
  maxDate,
  mealName,
  inputRef,
  onDateBlur,
  onTimeChange,
  onServingsChange,
  showDateTimeInputs = true,
  showServingsInput = true,
  initialShowMealNameEdit = false,
}) => {
  const playerRefEdit = useRef<Player>(null);
  const iconsIconPrimaryColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-primary-color')
      .trim();
    return color || '#000000';
  }, []);
  const { t } = useTranslator();
  const { errors } = useFormikContext<{ mealName?: string }>();
  const [showMealNameEdit, setShowMealNameEdit] = useState(initialShowMealNameEdit);
  const mealNameTextareaRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const {
    lotties: { editIcon },
  } = useAppContext();

  // Focus meal name textarea when edit mode is activated
  // useEffect(() => {
  //   if (showMealNameEdit) {
  //     const timer = setTimeout(() => {
  //       (mealNameTextareaRef.current as HTMLTextAreaElement)?.focus();
  //       const len = (mealNameTextareaRef.current as HTMLTextAreaElement)?.value.length;
  //       if (len) {
  //         (mealNameTextareaRef.current as HTMLTextAreaElement)?.setSelectionRange(len, len);
  //       }
  //     }, 400);
  //     return () => clearTimeout(timer);
  //   }
  // }, [showMealNameEdit]);

  // Auto-resize textarea for meal name field
  useEffect(() => {
    if (!showMealNameEdit) return;

    const textarea = mealNameTextareaRef.current as HTMLTextAreaElement | null;
    if (!textarea) return;

    const adjustTextareaHeight = () => {
      textarea.style.height = 'auto';
      const minHeight = 60;
      textarea.style.height = `${Math.max(textarea.scrollHeight, minHeight)}px`;
    };

    requestAnimationFrame(() => {
      adjustTextareaHeight();
    });

    textarea.addEventListener('input', adjustTextareaHeight);
    textarea.addEventListener('change', adjustTextareaHeight);
    window.addEventListener('resize', adjustTextareaHeight);

    return () => {
      textarea.removeEventListener('input', adjustTextareaHeight);
      textarea.removeEventListener('change', adjustTextareaHeight);
      window.removeEventListener('resize', adjustTextareaHeight);
    };
  }, [showMealNameEdit]);

  return (
    <>
      {showDateTimeInputs && (
        <div className="d-flex justify-content-between">
          <div className="d-flex">
            <div className="me-1 position-relative">
              <Input
                name="date"
                type="date"
                min={minDate}
                max={maxDate}
                translateErrMsg
                callbackOnChange={onDateBlur}
              />
              <div className={!showDateInput ? 'today' : 'd-none'}>{t('time.day.today')}</div>
            </div>
            <div className="me-1">
              <Input name="time" type="time" translateErrMsg callbackOnChange={onTimeChange} />
            </div>
          </div>
        </div>
      )}

      <div
        className={`d-flex justify-content-between align-items-start ${
          showDateTimeInputs ? 'mt-xxs' : ''
        }`}
      >
        {showMealNameEdit ? (
          <div className="w-100 text-end">
            <Button
              color="link"
              size="sm"
              className="p-0 mb-xxs"
              onClick={() => setShowMealNameEdit(false)}
              disabled={!!errors.mealName}
            >
              {t('mealDetails.notesBox.confirmButton')}
            </Button>
            <Input
              type="textarea"
              placeholder={t('tracker.mealDetails.input.placeholder')}
              name="mealName"
              className="w-100 mb-l"
              innerRef={mealNameTextareaRef}
              translateErrMsg
              style={{ resize: 'none', overflow: 'hidden', minHeight: '60px' }}
            />
          </div>
        ) : (
          <>
            <h2 className="title-large semi-bold mb-l">{mealName}</h2>
            <Button
              color="link"
              size="sm"
              className="p-0 ms-2 mt-2"
              onClick={() => setShowMealNameEdit(true)}
              onMouseEnter={() => playerRefEdit.current?.playFromBeginning()}
              onTouchStart={() => playerRefEdit.current?.playFromBeginning()}
            >
              <Player
                ref={playerRefEdit}
                icon={editIcon}
                size={20}
                colorize={iconsIconPrimaryColor}
              />
            </Button>
          </>
        )}
      </div>

      {showServingsInput && (
        <div className="mb-xl">
          <Input
            horizontal
            label={t('tracker.servings.title')}
            name="servings"
            type="number"
            translateErrMsg
            min={0.1}
            max={30}
            step={0.01}
            className="text-center"
            innerRef={inputRef}
            callbackOnChange={(value) => {
              const num = typeof value === 'string' ? parseFloat(value) : value;
              if (!Number.isNaN(num) && onServingsChange) onServingsChange(num);
            }}
          />
        </div>
      )}
    </>
  );
};

export default TrackerMealFormHeader;
